import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
import { requestIdleCallback } from '@theme/utilities';

/**
 * Collects section IDs from all cart-items-component elements on the page.
 * @returns {string} Comma-separated section IDs
 */
function getCartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      ids.add(el.dataset.sectionId);
    }
  });
  return Array.from(ids).join(',');
}

/**
 * Resolves the new TOTAL cart item count after an add.
 *
 * cart-icon.js sets the header bubble to `itemCount` for non product-form
 * sources, and the drawer now updates via hydration (which never re-renders the
 * header bubble). So we must pass the real total here, not the quantity added,
 * or the badge would show "1" regardless of cart size.
 *
 * @param {Record<string, string>|undefined} sections - Rendered sections from the cart/add response.
 * @returns {number} The new total cart item count.
 */
function getNewCartCount(sections) {
  // Authoritative: the count rendered into the returned section(s).
  if (sections) {
    for (const html of Object.values(sections)) {
      const text = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[ref="cartItemCount"]')
        ?.textContent?.trim();
      if (text) return parseInt(text, 10);
    }
  }

  // Fallback: increment the currently displayed header count by the 1 unit added.
  const current = parseInt(document.querySelector('[ref="cartBubbleCount"]')?.textContent ?? '0', 10);
  return (Number.isNaN(current) ? 0 : current) + 1;
}

// Handle ATC button clicks via event delegation
document.addEventListener('click', (event) => {
  const btn = /** @type {HTMLElement|null} */ (event.target?.closest('.upsell-add-btn'));
  if (!btn) return;

  const row = btn.closest('.upsell-product-row');
  const variantSelect = /** @type {HTMLSelectElement|null} */ (row?.querySelector('.upsell-variant-select'));
  const variantId = variantSelect?.value || btn.dataset.variantId;

  if (!variantId) return;

  const btnText = btn.querySelector('.btn-text');
  const originalText = btnText?.textContent ?? '';

  // The row is removed by the cart re-render on success, so this only ever runs
  // when the add failed and the button has to go back to being pressable.
  const resetButton = () => {
    if (btnText) btnText.textContent = originalText;
    btn.removeAttribute('disabled');
    btn.removeAttribute('aria-busy');
  };

  btn.setAttribute('disabled', '');
  btn.setAttribute('aria-busy', 'true');
  if (btnText) btnText.textContent = btn.dataset.addingText || originalText;

  const formData = new FormData();
  formData.set('id', variantId);
  formData.set('quantity', '1');

  const sections = getCartSectionIds();
  if (sections) formData.set('sections', sections);

  fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    body: formData,
  })
    .then((res) => res.json())
    .then((data) => {
      if (data.status) {
        resetButton();
        return;
      }

      btn.dispatchEvent(
        new CartAddEvent(data, variantId, {
          source: 'cart-upsell',
          itemCount: getNewCartCount(data.sections),
          variantId,
          sections: data.sections,
        })
      );
    })
    .catch((err) => {
      console.error('Upsell ATC error:', err);
      resetButton();
    });
});

// Sync variant select → ATC button data-variant-id
document.addEventListener('change', (event) => {
  const select = /** @type {HTMLSelectElement|null} */ (event.target?.closest('.upsell-variant-select'));
  if (!select) return;

  const row = select.closest('.upsell-product-row');
  const btn = /** @type {HTMLElement|null} */ (row?.querySelector('.upsell-add-btn'));
  if (btn) btn.dataset.variantId = select.value;

  // Swap the thumbnail to the chosen variant's image; a variant without its own
  // image keeps whatever the row rendered with.
  const image = select.selectedOptions[0]?.dataset.image;
  const img = row?.querySelector('.cart-items__media-image');
  if (image && img instanceof HTMLImageElement) {
    img.removeAttribute('srcset');
    img.src = image;
  }
});

const QUOTE_ENDPOINT = '/api/2026-07/graphql.json';
const QUOTE_MUTATION = `mutation UpsellQuote($country: CountryCode!, $input: CartInput!) @inContext(country: $country) {
  cartCreate(input: $input) {
    cart { totalQuantity cost { totalAmount { amount currencyCode } } }
  }
}`;
const QUOTE_TIMEOUT_MS = 5000;
const QUOTE_MAX_ROWS = 8;
const QUOTE_STORAGE_KEY = 'cart-upsell-quote';

/**
 * @typedef {object} QuoteRequest
 * @property {string} country
 * @property {string} currency
 * @property {number} total - The cart's own total, in minor units.
 * @property {number} count - The cart's item count.
 * @property {{ lines: object[] } & Record<string, unknown>} input - A Storefront API CartInput that rebuilds the cart.
 * @property {string[]} candidates - Variant ids to price, one added unit each.
 */

/** @typedef {Record<string, number> | null} QuoteResult What each candidate adds to the cart total, by variant id. */

/** @type {Map<string, Promise<QuoteResult>>} */
const pendingQuotes = new Map();

/**
 * Liquid's `json` writes line item properties as [key, value] pairs.
 * @param {[string, unknown][] | null | undefined} properties
 * @returns {{ key: string, value: string }[]}
 */
function toAttributes(properties) {
  return (properties ?? [])
    .filter(([key, value]) => key && value != null && value !== '')
    .map(([key, value]) => ({ key: String(key), value: String(value) }));
}

/**
 * Has Shopify build and price a cart nobody sees or checks out.
 * @param {QuoteRequest} request
 * @param {QuoteRequest['input']} input
 * @param {number} expectedCount
 * @param {AbortSignal} signal
 * @returns {Promise<number | null>} The cart's total in minor units, or null when Shopify didn't build the cart asked for.
 */
async function priceCart(request, input, expectedCount, signal) {
  try {
    const response = await fetch(QUOTE_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query: QUOTE_MUTATION, variables: { country: request.country, input } }),
      signal,
    });
    if (!response.ok) return null;

    const { data } = await response.json();
    const cart = data?.cartCreate?.cart;
    // Shopify drops a line it won't sell without failing, which would read as a free item.
    if (!cart || cart.totalQuantity !== expectedCount) return null;

    const { amount, currencyCode } = cart.cost.totalAmount;
    if (currencyCode !== request.currency) return null;
    return convertMoneyToMinorUnits(amount, request.currency);
  } catch {
    return null;
  }
}

/**
 * @param {QuoteRequest} request
 * @returns {Promise<QuoteResult>}
 */
async function fetchQuote(request) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), QUOTE_TIMEOUT_MS);

  /** @param {string} variantId */
  const withCandidate = (variantId) => ({
    ...request.input,
    lines: [...request.input.lines, { merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }],
  });

  // Separate requests: Shopify runs the mutations of one request one after another.
  const [baseTotal, ...totals] = await Promise.all([
    priceCart(request, request.input, request.count, controller.signal),
    ...request.candidates.map((id) => priceCart(request, withCandidate(id), request.count + 1, controller.signal)),
  ]);
  clearTimeout(timer);

  // The copy has to price the cart exactly as the cart itself does. A customer-only
  // discount, a code that didn't carry over or tax added on top all break that, and
  // every quote built on the copy would be off by the same amount.
  if (baseTotal == null || baseTotal !== request.total) return null;

  /** @type {Record<string, number>} */
  const increases = {};
  request.candidates.forEach((id, index) => {
    const total = totals[index];
    if (total != null) increases[id] = total - baseTotal;
  });
  return increases;
}

/**
 * @param {string} key
 * @returns {QuoteResult | undefined}
 */
function readStoredQuote(key) {
  try {
    const stored = JSON.parse(sessionStorage.getItem(QUOTE_STORAGE_KEY) ?? 'null');
    return stored?.key === key ? stored.increases : undefined;
  } catch {
    return undefined;
  }
}

/**
 * @param {string} key
 * @param {QuoteResult} increases
 */
function storeQuote(key, increases) {
  try {
    sessionStorage.setItem(QUOTE_STORAGE_KEY, JSON.stringify({ key, increases }));
  } catch {
    // Storage is a convenience: without it the quote is fetched again on the next page.
  }
}

/**
 * Whole percent saved, allowing one minor unit for Shopify rounding a discounted unit down.
 * @param {number} price
 * @param {number} increase
 */
function savingPercent(price, increase) {
  return Math.floor(((price - increase + 1) * 100) / price);
}

/**
 * Shows the real saving on in-cart upsell rows. Shopify prices a copy of the cart
 * with each suggestion added; a row drops below its full price only when what it
 * adds to the total is lower, and the refill heading names the best such saving.
 *
 * @extends {Component<{ quoteCart?: HTMLScriptElement, quoteSalePrice?: HTMLTemplateElement, quoteHeading?: HTMLElement, quoteRefillHeading?: HTMLTemplateElement }>}
 */
class CartUpsellQuote extends Component {
  /** @type {string | undefined} */
  #key;

  // A cart re-render morphs this element in place, and morph hands updatedCallback's
  // hook the parsed copy, which is never upgraded, so the hook never reaches this
  // element. Watching the subtree catches new cart data and rows reset to full price.
  #observer = new MutationObserver(() => this.#quote());

  connectedCallback() {
    super.connectedCallback();
    this.#observer.observe(this, { subtree: true, childList: true, characterData: true });
    this.#quote();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer.disconnect();
  }

  #quote() {
    const request = this.#request();
    if (!request) return;

    const key = JSON.stringify(request);
    this.#key = key;

    const stored = readStoredQuote(key);
    if (stored !== undefined) {
      this.#apply(stored, request.currency);
      return;
    }

    // The drawer and the /cart page body render this over the same cart, so they share one quote.
    let pending = pendingQuotes.get(key);
    if (!pending) {
      pending = new Promise((resolve) => requestIdleCallback(resolve))
        .then(() => fetchQuote(request))
        .then((increases) => {
          storeQuote(key, increases);
          return increases;
        });
      pendingQuotes.set(key, pending);
    }

    pending.then((increases) => {
      if (this.#key === key && this.isConnected) this.#apply(increases, request.currency);
    });
  }

  /** @returns {QuoteRequest | null} */
  #request() {
    const { quoteCart } = this.refs;
    if (!(quoteCart instanceof HTMLScriptElement)) return null;

    const variantIds = Array.from(this.querySelectorAll('[data-quote-price]'), (row) => row.getAttribute('data-variant-id') ?? '');
    const candidates = [...new Set(variantIds)].filter(Boolean).slice(0, QUOTE_MAX_ROWS);
    if (!candidates.length) return null;

    try {
      const cart = JSON.parse(quoteCart.textContent ?? '');
      return {
        country: cart.country,
        currency: cart.currency,
        total: cart.total,
        count: cart.count,
        input: {
          lines: cart.lines.map(
            /** @param {{ variant: number, quantity: number, plan: number | null, properties: [string, unknown][] }} line */
            (line) => ({
              merchandiseId: `gid://shopify/ProductVariant/${line.variant}`,
              quantity: line.quantity,
              ...(line.plan ? { sellingPlanId: `gid://shopify/SellingPlan/${line.plan}` } : {}),
              attributes: toAttributes(line.properties),
            })
          ),
          discountCodes: cart.codes,
          buyerIdentity: { countryCode: cart.country },
        },
        candidates,
      };
    } catch {
      return null;
    }
  }

  /**
   * @param {QuoteResult} increases
   * @param {string} currency
   */
  #apply(increases, currency) {
    const { quoteSalePrice, quoteHeading, quoteRefillHeading } = this.refs;
    const { moneyFormat } = this.dataset;
    if (!increases || !(quoteSalePrice instanceof HTMLTemplateElement) || !moneyFormat) return;

    let refillPercent = 0;

    for (const row of this.querySelectorAll('[data-quote-price]')) {
      const price = Number(row.getAttribute('data-quote-price'));
      const increase = increases[row.getAttribute('data-variant-id') ?? ''];
      if (increase === undefined || increase <= 0 || increase >= price) continue;

      const percent = savingPercent(price, increase);
      if (percent < 1) continue;
      if (row.hasAttribute('data-quote-refill')) refillPercent = Math.max(refillPercent, percent);

      const container = row.querySelector('.upsell-price-container');
      if (!container || container.querySelector('[data-price-slot]')) continue;
      const regular = container.querySelector('.upsell-current-price')?.textContent?.trim();
      if (!regular) continue;

      const sale = /** @type {DocumentFragment} */ (quoteSalePrice.content.cloneNode(true));
      const regularSlot = sale.querySelector('[data-price-slot="regular"]');
      const saleSlot = sale.querySelector('[data-price-slot="sale"]');
      if (!regularSlot || !saleSlot) continue;

      regularSlot.textContent = regular;
      saleSlot.textContent = formatMoney(increase, moneyFormat, currency);
      container.replaceChildren(sale);
    }

    if (refillPercent >= 1 && quoteHeading && quoteRefillHeading instanceof HTMLTemplateElement) {
      const heading = quoteRefillHeading.innerHTML.trim().replace('[percent]', String(refillPercent));
      if (quoteHeading.innerHTML !== heading) quoteHeading.innerHTML = heading;
    }

    // These writes are this element's own, not a re-render.
    this.#observer.takeRecords();
  }
}

if (!customElements.get('cart-upsell-quote')) {
  customElements.define('cart-upsell-quote', CartUpsellQuote);
}
